import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sun, Moon, Bell, Check, LogOut, Play, Pause, Square, Mail } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { setDayStatus } from "@/lib/dayStatus";
import { useApp } from "@/context/AppContext";
import { Avatar } from "@/components/common/Avatar";
import { IrisBrand } from "@/components/common/IrisBrand";
import { relative } from "@/lib/format";
import { cn } from "@/lib/utils";
import { LOGOUT } from "@/constants/testIds/auth";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

export function TopBar({ onOpenPalette }) {
  const { theme, toggleTheme, me, can, impersonate, logout } = useApp();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isSuperadmin = me?.role_id === "role-superadmin";
  const showMailbox = can("mailbox", "view");

  const { data: mailboxUnread } = useQuery({
    queryKey: ["mailbox-unread-count"], queryFn: () => get("/mailbox/unread-count"),
    enabled: showMailbox, refetchInterval: 60000,
  });
  const unreadMail = mailboxUnread?.count || 0;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const { data: notifs = [] } = useQuery({ queryKey: ["notifications"], queryFn: () => get("/notifications") });
  const unread = notifs.filter((n) => !n.read).length;

  const readAll = useMutation({
    mutationFn: () => post("/notifications/read-all"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const { data: todayAttendance } = useQuery({
    queryKey: ["attendance-today", me?.id], queryFn: () => get(`/hrms/attendance/today?employee_id=${me?.id}`), enabled: !!me,
  });
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [, forceTick] = useState(0);
  useEffect(() => { const t = setInterval(() => forceTick((n) => n + 1), 30000); return () => clearInterval(t); }, []);

  const openSegment = todayAttendance?.open_segment;
  const dayStarted = !!openSegment;
  const closedHoursToday = todayAttendance?.total_hours_today ?? 0;
  const liveElapsedHours = openSegment ? Math.max(0, (Date.now() - new Date(openSegment.check_in).getTime()) / 3600000) : 0;
  const totalHoursToday = closedHoursToday + liveElapsedHours;
  const hasStartedBefore = (todayAttendance?.segment_count ?? 0) > 0;
  useEffect(() => { if (todayAttendance !== undefined) setDayStatus(dayStarted); }, [todayAttendance, dayStarted]);

  const startDay = useMutation({
    mutationFn: () => post("/hrms/attendance/start", { employee_id: me?.id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["attendance-today", me?.id] }),
  });
  const stopDay = useMutation({
    mutationFn: () => post("/hrms/attendance/stop", { employee_id: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["attendance-today", me?.id] }); setStopConfirmOpen(false); },
  });

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-card px-4">
      <div className="flex items-center gap-2">
        <button
          data-testid="global-search-input"
          onClick={onOpenPalette}
          className="flex h-9 w-96 items-center gap-2 rounded-lg bg-muted px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/70"
        >
          <Search className="h-4 w-4" />
          <span>Search leads, projects, clients...</span>
          <kbd className="ml-auto rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[10px]">⌘K</kbd>
        </button>

        {showMailbox && (
          <button data-testid="mailbox-nav-btn" onClick={() => navigate("/mailbox")}
            className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground">
            <Mail className="h-4 w-4" />
            {unreadMail > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                {unreadMail > 99 ? "99+" : unreadMail}
              </span>
            )}
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        {totalHoursToday > 0 && (
          <span data-testid="today-total-hours" className="font-mono text-xs text-muted-foreground">
            Today: {totalHoursToday.toFixed(1)}h
          </span>
        )}
        {dayStarted ? (
          <>
            <Button size="sm" variant="outline" data-testid="pause-day-btn" onClick={() => stopDay.mutate()} disabled={stopDay.isPending}>
              <Pause className="mr-1.5 h-3.5 w-3.5" />Pause
            </Button>
            <Button size="sm" variant="destructive" data-testid="stop-day-btn" onClick={() => setStopConfirmOpen(true)}>
              <Square className="mr-1.5 h-3.5 w-3.5" />Stop Day
            </Button>
          </>
        ) : (
          <Button size="sm" data-testid="start-day-btn" onClick={() => startDay.mutate()} disabled={startDay.isPending}
            className="bg-[#3A86FF] text-white shadow-sm hover:bg-[#2f6fe0]">
            <Play className="mr-1.5 h-3.5 w-3.5" />{hasStartedBefore ? "Resume My Day" : "Start My Day"}
          </Button>
        )}
        <div className="h-5 w-px bg-border" />
        <LiveClock />
        <div className="h-5 w-px bg-border" />
      <div className="flex items-center gap-1">
        <button data-testid="theme-toggle" onClick={toggleTheme}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground">
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="notification-bell"
              className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground">
              <Bell className="h-4 w-4" />
              {unread > 0 && (
                <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                  {unread}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <span className="text-sm font-semibold">Notifications</span>
              <button data-testid="mark-all-read" onClick={() => readAll.mutate()}
                className="text-xs text-primary hover:underline">Mark all read</button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifs.map((n) => (
                <button key={n.id} data-testid={`notif-${n.id}`}
                  onClick={() => n.link && navigate(n.link)}
                  className="flex w-full gap-2 border-b border-border px-4 py-3 text-left hover:bg-muted">
                  <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", n.read ? "bg-transparent" : "bg-primary")} />
                  <div className="min-w-0">
                    <p className="text-[13px] leading-snug text-foreground">{n.message}</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">{relative(n.created_at)}</p>
                  </div>
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu" className="ml-1 flex items-center gap-2 rounded-lg p-1 hover:bg-muted">
              <Avatar user={me} size="md" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>
              <div className="font-semibold">{me?.name}</div>
              <div className="text-xs font-normal text-muted-foreground">{me?.email}</div>
              <div className="mt-1 text-xs font-normal text-primary">{me?.role?.name}</div>
            </DropdownMenuLabel>
            <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate("/profile")}>My Profile</DropdownMenuItem>
            <DropdownMenuSeparator />
            {isSuperadmin && (
              <>
                <DropdownMenuLabel className="text-xs text-muted-foreground">Impersonate User</DropdownMenuLabel>
                {users.map((u) => (
                  <DropdownMenuItem key={u.id} data-testid={`impersonate-user-${u.id}`} onClick={() => impersonate(u.id)}>
                    <Avatar user={u} size="sm" className="mr-2" />
                    <span className="flex-1 truncate">{u.name}</span>
                    {u.id === me?.id && <Check className="h-3.5 w-3.5 text-primary" />}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem data-testid={LOGOUT.button} onClick={handleLogout} className="text-red-600 dark:text-red-400">
              <LogOut className="mr-2 h-3.5 w-3.5" />
              Log out
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <div className="flex justify-center px-2 py-2">
              <IrisBrand className="gap-0.5" iconClassName="h-3.5 w-3.5" titleClassName="text-[10px]" taglineClassName="text-[9px]" />
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      </div>

      <AlertDialog open={stopConfirmOpen} onOpenChange={setStopConfirmOpen}>
        <AlertDialogContent data-testid="stop-day-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Stop your day?</AlertDialogTitle>
            <AlertDialogDescription>
              You've logged <span className="font-semibold text-foreground">{totalHoursToday.toFixed(1)} hours</span> today in total.
              You can still start again later today if needed — this just ends your current session.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="stop-day-confirm-btn" onClick={() => stopDay.mutate()} disabled={stopDay.isPending}>Stop My Day</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const date = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const time = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" });
  return (
    <div data-testid="live-clock" className="flex flex-col items-end leading-tight">
      <span className="text-xs font-semibold text-foreground">{date}</span>
      <span className="font-mono text-[11px] text-muted-foreground">{time}</span>
    </div>
  );
}
